import type { EstimateAssumption, EstimateExclusion, EstimateWarning, EstimatorInput } from "../../types/index.js";
import { getScopeMultiplier } from "../scope-multipliers.js";
import {
  applyItem,
  findItem,
  scaleAppliedItem,
  type AppliedLineItem,
  type ProjectCalculationOutput,
  type ProjectCalculatorFn
} from "./shared.js";

/**
 * The Deck & Patio Estimator (Phase 11). `deck_patio` is one project
 * type covering two structurally different builds - a raised or
 * ground-level wood/composite deck, and a ground-level poured/paved
 * patio - selected by `deck_patio_kind`. Per the brief's explicit "do
 * not use interior renovation assumptions" and "use appropriate
 * unit-based and scope-based calculations": nothing here reuses a
 * kitchen/basement category. Every scope addition (material, elevated
 * framing, stairs, railings, additional levels, roof/cover) is priced as
 * its own line item in the unit that actually matches how it's built -
 * sqft for surface/framing/roof, linear ft for railings, item for
 * stairs, allowance for demolition's percentage base and mechanical-
 * style categories - never one blanket per-square-foot multiplier.
 *
 * Structural-complexity handling: rather than a fixed per-record
 * complexity value, the user's own `deck_patio_structural_complexity`
 * answer scales the anchor line item via STRUCTURAL_COMPLEXITY_MULTIPLIERS,
 * the same "user-driven multiplier applied only to the anchor" pattern
 * basement's CONDITION_ENVELOPE_MULTIPLIERS established in Phase 8 - see
 * rules/basement.ts.
 */

const STRUCTURAL_COMPLEXITY_MULTIPLIERS: Record<string, number> = {
  standard: 1.0,
  some_complexity: 1.15,
  significant_engineering_required: 1.3
};

const DECK_MATERIAL_ITEM_BY_TIER: Record<string, string> = {
  pressure_treated: "deck_material_pressure_treated",
  cedar: "deck_material_cedar",
  composite: "deck_material_composite",
  premium_custom: "deck_material_premium"
};

const PATIO_MATERIAL_ITEM_BY_TIER: Record<string, string> = {
  concrete: "patio_material_concrete",
  pavers: "patio_material_pavers",
  natural_stone: "patio_material_natural_stone",
  premium_custom: "patio_material_premium"
};

const RAILING_ITEM_BY_TIER: Record<string, string | null> = {
  none: null,
  standard: "deck_patio_railing_standard",
  premium_glass_cable: "deck_patio_railing_premium"
};

const ROOF_ITEM_BY_TYPE: Record<string, string | null> = {
  none: null,
  pergola: "deck_patio_roof_pergola",
  solid_roof: "deck_patio_roof_solid"
};

/**
 * A deck/patio's railing run is naturally a function of its perimeter,
 * not its area, but the structured input only captures square footage -
 * same situation kitchen's cabinetry-per-sqft heuristic
 * (KITCHEN_CABINETRY_LINEAR_FT_PER_SQFT, see rules/shared.ts) is in, and
 * handled the same way: a named, documented, planning-level ratio
 * constant, floored at a sensible minimum, always surfaced as an
 * explicit assumption in the result rather than applied silently.
 */
const DECK_RAILING_LINEAR_FT_PER_SQFT = 0.55;

function deriveRailingLinearFeet(sqft: number): number {
  return Math.max(16, Math.round(sqft * DECK_RAILING_LINEAR_FT_PER_SQFT));
}

function rawString(input: EstimatorInput, key: string): string | undefined {
  const value = input.rawAnswers[key];
  return typeof value === "string" ? value : undefined;
}

export const calculateDeckPatio: ProjectCalculatorFn = (input, catalog, regionalMultiplier) => {
  const applied: AppliedLineItem[] = [];
  const assumptions: EstimateAssumption[] = [];
  const exclusions: EstimateExclusion[] = [];
  const warnings: EstimateWarning[] = [];
  let anchorMissing = false;

  const finishLevel = input.finishLevel;
  const sqft = input.roomSize?.value;
  const kind = rawString(input, "deck_patio_kind") ?? "deck";
  const isDeck = kind === "deck";

  let baseTypicalForPercentage = 0;

  if (sqft && sqft > 0) {
    // --- Base construction (anchor) + scope + structural-complexity adjustment ---
    const baseItemId = isDeck ? "deck_patio_deck_base" : "deck_patio_patio_base";
    const base = applyItem(catalog, baseItemId, { quantity: sqft, finishLevel, regionalMultiplier });
    if (base) {
      const scopeMultiplier = getScopeMultiplier(input.scope);
      let scoped = scaleAppliedItem(base, scopeMultiplier);
      assumptions.push({
        code: "scope_multiplier_applied",
        message: `Base construction cost adjusted by a ${scopeMultiplier}x scope multiplier for "${input.scope}".`
      });

      const complexity = rawString(input, "deck_patio_structural_complexity") ?? "standard";
      const complexityMultiplier = STRUCTURAL_COMPLEXITY_MULTIPLIERS[complexity] ?? 1.0;
      if (complexityMultiplier !== 1.0) {
        scoped = scaleAppliedItem(scoped, complexityMultiplier);
        assumptions.push({
          code: "structural_complexity_multiplier_applied",
          message: `Base construction cost further adjusted by a ${complexityMultiplier}x multiplier for "${complexity}" structural complexity.`
        });
      }

      applied.push(scoped);
      baseTypicalForPercentage = scoped.result.typical?.amount ?? 0;
    }

    // --- Surface material (kind-specific tier) ------------------------------
    if (isDeck) {
      const material = rawString(input, "deck_patio_material");
      const materialItemId = material ? DECK_MATERIAL_ITEM_BY_TIER[material] : "deck_material_pressure_treated";
      if (materialItemId) {
        const m = applyItem(catalog, materialItemId, { quantity: sqft, finishLevel, regionalMultiplier });
        if (m) applied.push(m);
        if (!material) {
          assumptions.push({ code: "deck_material_defaulted", message: "No decking material was selected - assumed pressure-treated wood, the most common baseline material." });
        }
      }
    } else {
      const material = rawString(input, "deck_patio_patio_material");
      const materialItemId = material ? PATIO_MATERIAL_ITEM_BY_TIER[material] : "patio_material_concrete";
      if (materialItemId) {
        const m = applyItem(catalog, materialItemId, { quantity: sqft, finishLevel, regionalMultiplier });
        if (m) applied.push(m);
        if (!material) {
          assumptions.push({ code: "patio_material_defaulted", message: "No patio surface material was selected - assumed poured concrete, the most common baseline material." });
        }
      }
    }

    // --- Elevated framing (deck only) ---------------------------------------
    if (isDeck && rawString(input, "deck_patio_elevation") === "elevated") {
      const elevated = applyItem(catalog, "deck_elevated_framing", { quantity: sqft, finishLevel, regionalMultiplier });
      if (elevated) applied.push(elevated);
    }

    // --- Roof / cover --------------------------------------------------------
    const roof = rawString(input, "deck_patio_roof_cover") ?? "none";
    const roofItemId = ROOF_ITEM_BY_TYPE[roof] ?? null;
    if (roofItemId) {
      const r = applyItem(catalog, roofItemId, { quantity: sqft, finishLevel, regionalMultiplier });
      if (r) applied.push(r);
    } else {
      exclusions.push({ code: "excludes_deck_patio_roof_cover", message: "No roof/cover structure was requested - excluded from this estimate." });
    }
  } else {
    anchorMissing = true;
  }

  // --- Stairs --------------------------------------------------------------
  const stairs = rawString(input, "deck_patio_stairs") ?? "none";
  const stairsQuantity = stairs === "one_set" ? 1 : stairs === "two_or_more_sets" ? 2 : 0;
  if (stairsQuantity > 0) {
    const s = applyItem(catalog, "deck_patio_stairs", { quantity: stairsQuantity, finishLevel, regionalMultiplier });
    if (s) applied.push(s);
    if (stairs === "two_or_more_sets") {
      assumptions.push({ code: "stairs_quantity_assumed", message: "\"Two or more\" stair sets was priced as exactly two sets for planning purposes - adjust once the exact count is known." });
    }
  } else {
    exclusions.push({ code: "excludes_deck_patio_stairs", message: "No stairs were requested - excluded from this estimate." });
  }

  // --- Railings --------------------------------------------------------------
  const railingTier = rawString(input, "deck_patio_railings") ?? "none";
  const railingItemId = RAILING_ITEM_BY_TIER[railingTier] ?? null;
  if (railingItemId && sqft && sqft > 0) {
    const linearFeet = deriveRailingLinearFeet(sqft);
    const r = applyItem(catalog, railingItemId, { quantity: linearFeet, finishLevel, regionalMultiplier });
    if (r) applied.push(r);
    assumptions.push({
      code: "railing_linear_footage_derived",
      message: `Railing length estimated at ${linearFeet} linear ft from the deck/patio's square footage - a planning-level heuristic, not an exact measurement.`
    });
  } else {
    exclusions.push({ code: "excludes_deck_patio_railings", message: "No railings were requested - excluded from this estimate." });
  }

  // Safety cross-check: an elevated deck with no railings is very likely non-compliant.
  if (isDeck && rawString(input, "deck_patio_elevation") === "elevated" && railingTier === "none") {
    warnings.push({
      code: "deck_elevated_without_railings",
      message:
        "An elevated deck was indicated without railings. Most Canadian building codes require a guard/railing above a certain height - this was not priced or confirmed and should be verified with your municipality before finalizing budget.",
      severity: "caution"
    });
  }

  // --- Multiple levels ---------------------------------------------------------
  const levels = rawString(input, "deck_patio_levels") ?? "single_level";
  const levelQuantity = levels === "two_levels" ? 1 : levels === "three_plus_levels" ? 2 : 0;
  if (levelQuantity > 0) {
    const lvl = applyItem(catalog, "deck_patio_additional_level", { quantity: levelQuantity, finishLevel, regionalMultiplier });
    if (lvl) applied.push(lvl);
  } else {
    exclusions.push({ code: "excludes_deck_patio_multiple_levels", message: "A single-level design was assumed - no additional-level cost was applied." });
  }

  // --- Demolition (existing common flag) + replacement cross-check -----------
  if (input.workType.demolitionRequired === "yes" && findItem(catalog, "deck_patio_demolition")) {
    const d = applyItem(catalog, "deck_patio_demolition", { regionalMultiplier, baseAmountForPercentage: baseTypicalForPercentage });
    if (d) applied.push(d);
  }
  const constructionType = rawString(input, "deck_patio_construction_type");
  if (constructionType === "replacement" && input.workType.demolitionRequired !== "yes") {
    warnings.push({
      code: "deck_patio_replacement_without_demolition",
      message:
        "This was marked as a replacement of an existing deck/patio, but demolition wasn't marked as required - removing the existing structure/surface is usually necessary before rebuilding. Confirm this is intentional.",
      severity: "caution"
    });
  }

  // --- Accessibility -----------------------------------------------------------
  if (input.accessibility.required === "yes") {
    const a = applyItem(catalog, "deck_patio_accessibility", { finishLevel, regionalMultiplier });
    if (a) applied.push(a);
  }

  // --- Permit / design allowance -------------------------------------------------
  if (input.designAndPermits.needsDesignServices === "yes" || input.designAndPermits.permitLikelyRequired !== "no") {
    const permitDesign = applyItem(catalog, "deck_patio_permit_design", { finishLevel, regionalMultiplier });
    if (permitDesign) applied.push(permitDesign);
  }

  // --- Special handling: standing, deck/patio-specific disclosures -------------
  // Per the same "surface this clearly, don't leave it implicit" discipline
  // basement's special-handling warnings established in Phase 8: none of
  // these can be resolved from a self-reported questionnaire alone.
  if (isDeck) {
    warnings.push({
      code: "deck_footing_frost_depth_unknown",
      message:
        "Footing depth and soil/frost-line conditions can't be assessed from a questionnaire - required footing depth varies by municipality and site. This estimate assumes standard footing depth for the region; a site/soil assessment may be needed, especially for a larger or elevated deck.",
      severity: "caution"
    });
    if (rawString(input, "deck_patio_elevation") === "elevated") {
      warnings.push({
        code: "deck_elevated_permit_notice",
        message:
          "Elevated decks typically require a building permit and, above a certain height, engineered/stamped drawings for guards and structural support. Confirm requirements with your local building department before finalizing budget.",
        severity: "caution"
      });
    }
  } else {
    warnings.push({
      code: "patio_site_grading_drainage_unknown",
      message:
        "Site grading, drainage, and sub-base/soil conditions can't be assessed from a questionnaire - unexpected excavation, drainage correction, or soil stabilization needs discovered on-site may increase cost.",
      severity: "caution"
    });
  }

  if (rawString(input, "deck_patio_structural_complexity") === "significant_engineering_required") {
    assumptions.push({
      code: "deck_patio_structural_engineering_flag",
      message:
        "Significant structural complexity was indicated (e.g. substantial height, long spans, attaching to an existing structure, or building over/around obstacles). This estimate does not include engineering costs - a structural engineer's assessment and stamped drawings are likely required before final pricing."
    });
  }

  const output: ProjectCalculationOutput = { applied, assumptions, exclusions, anchorMissing, warnings };
  return output;
};
